/**
 * A bottom sheet. NFR-06, NFR-10.
 *
 * Hand-rolled rather than pulled from a library, for two reasons: the
 * dependency budget (NFR-13), and the fact that a dialog is one of the few
 * widgets where the accessible behaviour is short enough to write correctly —
 * focus trap, focus restore, Escape, `aria-modal`, and a labelled heading.
 *
 * Bottom-anchored because this app is thumb-first: the sheet's controls should
 * land where a thumb already is, not at the top of a phone the user is holding
 * one-handed.
 */
import { useCallback, useEffect, useRef, type ReactNode } from 'react'

export interface SheetProps {
  open: boolean
  onClose: () => void
  /** Accessible name for the dialog. Required — an unlabelled dialog is a trap. */
  title: string
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Sheet({ open, onClose, title, children }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return

      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab' || panel.current === null) return

      // Focus trap. Without it, Tab walks out of the dialog into the page
      // behind, which for a screen-reader user means silently losing the thing
      // they were editing.
      const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (focusable.length === 0) return

      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      const active = document.activeElement

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [open, onClose],
  )

  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement as HTMLElement | null

    const focusable = panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
    focusable?.[0]?.focus()

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Return focus where it was. A dialog that dumps focus on <body> leaves a
      // keyboard user at the top of the document with no idea where they are.
      restoreTo.current?.focus()
    }
  }, [open, onKeyDown])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        // The backdrop is a convenience for pointer users; Escape and the close
        // button are the accessible paths, so this is hidden from the tree
        // rather than presented as a second unlabelled button.
        aria-hidden="true"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface-raised p-4 shadow-xl sm:rounded-2xl"
      >
        {children}
      </div>
    </div>
  )
}
